import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { TextInput } from '../../../ui/input/text_input/text_input';
import { DatePicker } from '../../../ui/input/date_picker/date_picker';
import { ToggleInput } from '../../../ui/input/toggle_input/toggle_input';

export interface ILicenseDateValue {
  startDate: string | null;
  endDate: string | null;
}

interface ILicenseCardProps {
  title: string;
  iconPath: string;
  iconClassName: string;
  isSpecialIcon?: boolean;
  active: boolean;
  onToggle: () => void;
  startDate: string | null;
  endDate: string | null;
  onStartDateChange: (value: ILicenseDateValue) => void;
  onEndDateChange: (value: ILicenseDateValue) => void;
  creditUnit?: string;
  monthlyCredits?: string;
  onMonthlyCreditsChange?: (value: string) => void;
}

export const LicenseCard: React.FC<ILicenseCardProps> = ({
  title,
  iconPath,
  iconClassName,
  isSpecialIcon = false,
  active,
  onToggle,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  creditUnit,
  monthlyCredits,
  onMonthlyCreditsChange,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 3,
      borderRadius: 4,
      transition: 'all 0.2s ease-in-out',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: active ? 'background.paper' : 'action.hover',
      opacity: active ? 1 : 0.65,
      '&:hover': active ? { boxShadow: 2 } : {},
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box
          className={iconClassName}
          sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          {isSpecialIcon ? (
            <svg style={{ width: 24, height: 24 }} fill="none" viewBox="0 0 32 32" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M11 6h10a2 2 0 012 2v16a2 2 0 01-2 2H11a2 2 0 01-2-2V8a2 2 0 012-2zM11 12h10M11 16h10M11 20h10"
              />
            </svg>
          ) : (
            <svg style={{ width: 24, height: 24 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
            </svg>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mt: 0.25 }}
          >
            Premium Service
          </Typography>
        </Box>
      </Box>
      <ToggleInput toggled={active} handleToggle={onToggle} name={title} />
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 1.5,
        mt: 'auto',
        pointerEvents: !active ? 'none' : 'auto',
      }}
    >
      <DatePicker
        label="Start Date"
        asSingle={true}
        useRange={false}
        startDate={startDate}
        endDate={startDate}
        onChange={onStartDateChange}
      />
      <DatePicker
        label="End Date"
        asSingle={true}
        useRange={false}
        startDate={endDate}
        endDate={endDate}
        onChange={onEndDateChange}
      />
    </Box>
    {creditUnit && onMonthlyCreditsChange && (
      <Box sx={{ mt: 1.5, pointerEvents: !active ? 'none' : 'auto' }}>
        <TextInput
          label={`Credits / month (${creditUnit})`}
          name={`${title}-monthly-credits`}
          type="number"
          min="0"
          placeholder="Unlimited"
          value={monthlyCredits ?? ''}
          onChange={(e) => onMonthlyCreditsChange(e.target.value)}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mt: 0.5 }}>
          Blank = unlimited · 0 = uploads blocked
        </Typography>
      </Box>
    )}
  </Paper>
);
